import { format, isValid } from 'date-fns'
import { IUpsertCoachProfileMutationVariables } from 'types'
import { ICoachProfileFormValues } from './types'

const trimmed = (value: string): string | undefined => {
  const next = value.trim()
  return next.length > 0 ? next : undefined
}

// Maps form values to upsertCoachProfile variables, omitting empty fields so a
// partial draft save never overwrites existing data with blanks (the backend
// preserves any param that is not passed). Files are forwarded only when newly
// selected.
export const buildUpsertVariables = (
  values: ICoachProfileFormValues
): IUpsertCoachProfileMutationVariables => {
  const variables: IUpsertCoachProfileMutationVariables = {}

  const firstName = trimmed(values.firstName)
  if (firstName) variables.firstName = firstName

  const lastName = trimmed(values.lastName)
  if (lastName) variables.lastName = lastName

  const credentials = trimmed(values.credentials)
  if (credentials) variables.credentials = credentials

  const bio = trimmed(values.bio)
  if (bio) variables.bio = bio

  const stateOfResidence = trimmed(values.stateOfResidence)
  if (stateOfResidence) variables.stateOfResidence = stateOfResidence

  const ethnicity = trimmed(values.ethnicity)
  if (ethnicity) variables.ethnicity = ethnicity

  const additionalLanguages = trimmed(values.additionalLanguages)
  if (additionalLanguages) variables.additionalLanguages = additionalLanguages

  const cdrCnsNumber = trimmed(values.cdrCnsNumber)
  if (cdrCnsNumber) variables.cdrCnsNumber = cdrCnsNumber

  if (values.gender) variables.gender = values.gender

  if (values.cdrCnsExpirationDate && isValid(values.cdrCnsExpirationDate)) {
    variables.cdrCnsExpirationDate = format(values.cdrCnsExpirationDate, 'yyyy-MM-dd')
  }

  if (values.specialties.length > 0) variables.specialties = values.specialties

  if (values.photo) variables.photo = values.photo
  if (values.cdrCnsCard) variables.cdrCnsCard = values.cdrCnsCard

  // Send every complete licence row. Rows that are still mid-edit (no state, no
  // expiration date, or no document yet) are silently dropped — draft saves bypass
  // validation, so we have to enforce completeness here. A row with an existing document
  // URL but no newly-picked file still qualifies; we just omit the `document` field so
  // the backend preserves the existing attachment. We always send the array (even when
  // empty) because the backend SyncService replaces the full set: omitting it would mean
  // the coach could never remove a saved licence by deleting the row + saving draft.
  // Previously saved rows always re-hydrate with `existingDocumentUrl`, so an untouched
  // form still passes the same set back through and produces a no-op sync.
  variables.stateLicenceDetails = values.stateLicences
    .filter(
      (row) =>
        row.state &&
        row.expirationDate &&
        isValid(row.expirationDate) &&
        (row.file || row.existingDocumentUrl)
    )
    .map((row) => ({
      state: row.state as Exclude<typeof row.state, ''>,
      expirationDate: format(row.expirationDate as Date, 'yyyy-MM-dd'),
      expirationReminderEnabled: row.expirationReminderEnabled,
      document: row.file ?? undefined
    }))

  return variables
}
